import styled from '@emotion/styled';
import React, {
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ChevronSmallDown,
  ChevronSmallUp,
  LoadingIcon,
} from '@ELib/assets/images';
import {mixinCenterWrapper, mixinSVGIcon} from '@ELib/styles/mixins';
import {isAncestorElementOf} from '@ELib/utilities/dom';

import {AnimationWrapper} from '../AnimationWrapper';
import {NETransparentIconButton} from '../EButtons';

/**
 * An entirely empirical value to make sure loading triggers when scrolling up
 * directly after navigating to a specific row.
 *
 * The key is to convince IntersectionObserver that the load trigger is not fully
 * visible. Otherwise, no more events are fired when the user scrolls up.
 */
const LOAD_TRIGGER_HEIGHT = '4rem';

/**
 * Component that is responsible for showing a loading indicator
 * and lazily-loading new data on scroll. Note that there are
 * different paging mechanisms. More information can be found at
 * https://lexian-ag.atlassian.net/wiki/spaces/LEXDEV/pages/1002231/Pagination+Mechanics
 */
export const LoadTrigger = ({
  isTop = false,
  tableRef,
  isSelectable,
  hasExpansion,
  columnsLength,
  onLoad,
  onLoadFinished,
  numRows,
}: {
  /** If set to true, this component is expected to be the top (not bottom) load trigger. */
  isTop?: boolean;
  tableRef: RefObject<HTMLTableElement>;
  /** Flag whether the table has a selection column. */
  isSelectable: boolean;
  /** Flag whether the table has an expansion column. */
  hasExpansion: boolean;
  /** Number of table columns. */
  columnsLength: number;
  /** Custom loading handler. *Must* only resolve when the new data has finished loading. */
  onLoad: () => Promise<void>;
  onLoadFinished?: () => void;
  numRows: number;
}): React.ReactElement => {
  // Set the initial scroll position so that the load trigger is not visible initially.
  const tableParentRef = tableRef.current?.parentElement;
  const loadTriggerRef = useRef<HTMLTableRowElement>(null);
  const [isInitialScrollingDone, setIsInitialScrollingDone] = useState(false);
  useEffect(() => {
    if (isTop && !isInitialScrollingDone && tableParentRef) {
      tableParentRef.scrollTop = loadTriggerRef.current?.scrollHeight ?? 0;
      setIsInitialScrollingDone(true);
    }
  }, [isInitialScrollingDone, isTop, tableParentRef]);

  // declare intersection callback function
  const [isLoading, setIsLoading] = useState(false);
  const [scrollingInfo, setScrollingInfo] = useState<{
    id: string;
    position: number;
    numRowsOnLoad: number;
  }>();

  const doLoad = useCallback(async () => {
    setIsLoading(true);

    /**
     * Element that is used as a scroll anchoring reference. If the top LoadTrigger
     * is visible, this will be the first data row. If the bottom LoadTrigger is
     * visible, this will be the last data row.
     * On triggering a load, the scroll position of the scroll anchor element is
     * kept in memory. After the data has finished loading, the scroll position is
     * set according to the *new* position of the scroll anchor element.
     */
    const scrollAnchorRow = isTop
      ? loadTriggerRef.current!.nextElementSibling!
      : loadTriggerRef.current!.previousElementSibling!;
    const scrollAnchorId = scrollAnchorRow.getAttribute('data-id');
    const scrollAnchorPosition = scrollAnchorRow.getBoundingClientRect().top;

    if (scrollAnchorId) {
      setScrollingInfo({
        id: scrollAnchorId,
        position: scrollAnchorPosition,
        numRowsOnLoad: numRows,
      });
    }

    await onLoad();
  }, [isTop, onLoad, numRows]);

  useEffect(() => {
    if (!scrollingInfo || scrollingInfo.numRowsOnLoad === numRows) {
      return;
    }

    // set the element again as re-rendering the row list removes the previous element from the DOM
    const scrollAnchorRow = tableRef.current!.querySelector(
      `[data-id="${scrollingInfo.id}"]`
    )!;
    const currentPosition = scrollAnchorRow.getBoundingClientRect().top;
    const scrollBy = currentPosition - scrollingInfo.position;
    tableParentRef!.scrollBy({top: scrollBy});
    onLoadFinished?.();

    setScrollingInfo(undefined);
    setIsLoading(false);
  }, [onLoadFinished, scrollingInfo, tableParentRef, tableRef, numRows]);

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const intersectionCallback: IntersectionObserverCallback = useCallback(
    async (entries: IntersectionObserverEntry[]) => {
      if (entries.length === 0) {
        return;
      }

      const {boundingClientRect, isIntersecting} = entries[0]!;

      if (!isIntersecting || isLoading) {
        return;
      }

      // The top load trigger can be covered by the sticky header row.
      // It is therefore necessary to check whether the load trigger
      // is actually visible to the user already. Only when it is
      // actually visible, the lazy-loading should start.
      if (
        isTop &&
        !isTriggerVisible(loadTriggerRef.current, boundingClientRect)
      ) {
        return;
      }

      await doLoad();
    },
    [doLoad, isLoading, isTop]
  );

  // set up intersection observer to detect visible load trigger
  useEffect(() => {
    const loadTriggerEl = loadTriggerRef.current;
    if (!loadTriggerEl) {
      return;
    }
    const observer = new IntersectionObserver(intersectionCallback, {
      root: tableParentRef,
      // Having multiple thresholds is necessary as the top load trigger may be
      // covered by the table header row at the first intersection. So further
      // intersection events will be required to be able to properly detect
      // the appearance of the load trigger.
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
    });
    observer.observe(loadTriggerEl);
    return () => {
      observer.unobserve(loadTriggerEl);
    };
  }, [intersectionCallback, tableParentRef]);

  const colSpan = columnsLength - Number(isSelectable) - Number(hasExpansion);
  return (
    <tr
      ref={loadTriggerRef}
      className='noHover'
      style={{height: LOAD_TRIGGER_HEIGHT}}
    >
      {isSelectable && <td className='selection' />}
      {hasExpansion && <td />}
      <td colSpan={colSpan}>
        <CenteringWrapper>
          {isLoading ? (
            <AnimationWrapper>
              <LoadingIcon />
            </AnimationWrapper>
          ) : isTop ? (
            <NETransparentIconButton
              onClick={() => {
                doLoad();
              }}
            >
              <ChevronSmallUp />
            </NETransparentIconButton>
          ) : (
            <NETransparentIconButton
              onClick={() => {
                doLoad();
              }}
            >
              <ChevronSmallDown />
            </NETransparentIconButton>
          )}
        </CenteringWrapper>
      </td>
    </tr>
  );
};

const TRIGGER_IS_VISIBLE_X_STEP_SIZE = 100;

const isTriggerVisible = (
  loadTrigger: HTMLTableRowElement | null,
  boundingClientRect: DOMRectReadOnly
): boolean => {
  if (!loadTrigger) {
    return false;
  }

  let xPosition = boundingClientRect.left + TRIGGER_IS_VISIBLE_X_STEP_SIZE;

  while (xPosition < boundingClientRect.right) {
    /** Top-most visible element given the coordinates. */
    const elementAtBoundingRect = document.elementFromPoint(
      xPosition,
      // decrementing seems necessary, otherwise the wrong element is selected
      boundingClientRect.bottom - 1
    );

    const isVisible = isAncestorElementOf(loadTrigger, elementAtBoundingRect);

    if (isVisible) {
      return true;
    }

    xPosition += TRIGGER_IS_VISIBLE_X_STEP_SIZE;
  }

  return false;
};

const CenteringWrapper = styled.div`
  ${mixinCenterWrapper}
  && {
    opacity: 1;
  }

  svg {
    color: var(--core-core-grey);
    ${mixinSVGIcon}
  }
`;
